import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isPasskeySupported, loginWithPasskey } from '../lib/passkey';

type Step = 'request' | 'verify';

/**
 * Sign-in page. v4 (post-password) — three passwordless paths:
 *   1. Email-code (primary, default-rendered)
 *   2. Google
 *   3. Passkey (when the browser supports WebAuthn)
 *
 * "אין לך חשבון? הירשם" routes to /register (the v4 wizard).
 */
export function SignIn() {
  const { t } = useTranslation(['auth', 'passkey', 'common']);
  const navigate = useNavigate();
  const { signInWithGoogle, signInWithCustom } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('request');
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handlePasskey() {
    setError(null);
    setBusy(true);
    try {
      const { customToken } = await loginWithPasskey();
      await signInWithCustom(customToken);
    } catch (err) {
      const msg = (err as Error)?.message ?? '';
      // User-cancelled passkey prompt isn't really an error.
      if (/cancel|abort|NotAllowed/i.test(msg)) {
        setBusy(false);
        return;
      }
      setError(msg || t('passkey:errors.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function handleSendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setRetryAfter(null);
    setBusy(true);
    try {
      const res = await apiFetch<{ ok: true; sentTo: string }>('/api/auth/email/start', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      setStep('verify');
      setInfo(t('email_code.sent_to', { masked: res.sentTo }));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'rate_limited') {
        const wait = (err.body as { retryAfterSeconds?: number } | null)?.retryAfterSeconds ?? 60;
        setRetryAfter(wait);
        setError(t('email_code.errors.rate_limited', { seconds: wait }));
      } else {
        const code = err instanceof ApiError ? err.code : 'unknown';
        const key = `email_code.errors.${code}`;
        const translated = t(key);
        setError(translated === key ? (err as Error).message : translated);
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await apiFetch<{ customToken: string }>('/api/auth/email/verify', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      await signInWithCustom(res.customToken);
      // onAuthStateChanged picks it up and navigates away.
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      const key = `email_code.errors.${code}`;
      const translated = t(key);
      setError(translated === key ? (err as Error).message : translated);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="signin-wrap">
      <div className="signin-card">
        <h1 className="brand">{t('common:app.name')}</h1>
        <p className="brand-sub">
          {step === 'request' ? t('signin.title') : t('email_code.title')}
        </p>

        {step === 'request' ? (
          <form onSubmit={handleSendCode} className="signin-form">
            <label>
              <span>{t('signin.email')}</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={busy}
              />
            </label>
            {error && <div className="signin-error">{error}</div>}
            {retryAfter !== null && retryAfter > 0 && (
              <div className="signin-error">
                {t('email_code.wait_seconds', { seconds: retryAfter })}
              </div>
            )}
            <button type="submit" disabled={busy} className="btn-primary">
              {busy ? t('common:actions.loading_dots') : t('email_code.send_code')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode} className="signin-form">
            {info && <div className="signin-info">{info}</div>}
            <label>
              <span>{t('email_code.code_label')}</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                disabled={busy}
                autoFocus
              />
            </label>
            {error && <div className="signin-error">{error}</div>}
            <button type="submit" disabled={busy || code.length !== 6} className="btn-primary">
              {busy ? t('common:actions.loading_dots') : t('email_code.verify')}
            </button>
            <button
              type="button"
              className="signin-toggle"
              onClick={() => {
                setStep('request');
                setCode('');
                setError(null);
              }}
            >
              {t('email_code.different_email')}
            </button>
          </form>
        )}

        <div className="signin-divider">{t('common:actions.or')}</div>

        <button onClick={handleGoogle} disabled={busy} className="btn-google">
          {t('signin.google')}
        </button>

        {isPasskeySupported() && (
          <button
            type="button"
            className="btn-secondary"
            onClick={handlePasskey}
            disabled={busy}
          >
            🔑 {t('passkey:signin_button')}
          </button>
        )}

        <button
          type="button"
          className="signin-toggle"
          onClick={() => navigate('/register')}
        >
          {t('signin.toggle_to_signup')}
        </button>
      </div>
    </main>
  );
}
